import { useEffect, useState } from 'react';
import { Card, Collapse } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { find, sumBy } from 'lodash';
import { format, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import {
  getReservationsByProductType,
  getTotalGrossInCents,
  price,
  showUpSell,
  someExperienceIncludesProductType,
} from 'utils/helpers';
import {
  FORMAT_TIME_ONLY,
  FORMAT_WEEKDAY_DATE_MINI,
} from 'utils/helpers/datetime';
import { useCurrentBookingState } from 'utils/hooks/use_current_booking_state';
import LoadingSpinner from 'components/loading_spinner/loading_spinner';
import { deleteReservation } from 'redux/actions/reservation';
import { getAllPassengerTypes } from 'redux/selectors/booking_form';
import { getCurrentBooking } from 'redux/selectors/current_booking';

const passengersCounts = ({ currentBooking, passengers, passengerTypes }) => {
  const counts = {};

  passengers.forEach((id) => {
    const bookingPassenger = find(currentBooking.passengers_attributes, {
      id,
    });

    const type = find(passengerTypes, {
      id: bookingPassenger.passenger_type_id,
    });

    if (!counts[type.name]) {
      counts[type.name] = 0;
    }

    counts[type.name] += 1;
  });

  return Object.keys(counts).map((key) => ({ key, value: counts[key] }));
};

const FormattedTime = ({ date, time }) => {
  let formattedString = '';

  if (date) {
    formattedString += format(parseISO(date), FORMAT_WEEKDAY_DATE_MINI);

    if (time) {
      formattedString += ` at ${format(
        new Date(time.replace(/Z$/, '')),
        FORMAT_TIME_ONLY
      )}`;
    }
  }

  return <>{formattedString}</>;
};

const ExperienceIncludesAccommodation = () => (
  <Card.Body className="card-body--no-data text-grey">
    Your experience includes accommodation
  </Card.Body>
);

const DeletingReservation = () => (
  <Card.Body className="card-body--no-data d-flex align-items-center justify-content-center">
    <LoadingSpinner text="Deleting reservation..." />
  </Card.Body>
);

const ReservationsList = ({
  accommodations,
  currentBooking,
  passengerTypes,
  handleRemove,
  isFullyPaid,
  isActive,
}) => (
  <Card.Body>
    {accommodations.map((accommodation, accommodationIndex) => (
      <div key={`accomodation-${accommodation.id}`}>
        <div className="d-flex justify-content-end mb-3">
          {!isFullyPaid && !isActive && (
            <div
              role="button"
              onClick={() => handleRemove(accommodation.id)}
              className="link-danger text-decoration-underline text-small"
            >
              Remove
            </div>
          )}
        </div>
        <div className="fw-bold mb-2">{accommodation.selection_name}</div>
        {accommodation.subtitle && <p>{accommodation.subtitle}</p>}
        {accommodation.passenger_ids &&
          accommodation.passenger_ids.length > 0 && (
            <>
              <hr />
              <div className="mb-3 clearfix text-small">
                <p className="fw-bold mb-2">Guests</p>
                {passengersCounts({
                  currentBooking,
                  passengers: accommodation.passenger_ids,
                  passengerTypes,
                }).map((passenger) => (
                  <div
                    className="d-flex align-items-center mb-1"
                    key={`passenger-${passenger.key}`}
                  >
                    <i className="fas fa-user-friends card-body__icon" />
                    <span className="card-body__count-name ps-2">
                      {pluralize(passenger.key, passenger.value, true)}
                    </span>
                  </div>
                ))}

                {!!accommodation.pre_adjusted_commission_in_cents && (
                  <div className="d-flex justify-content-between text-small">
                    <div>
                      <div className="d-flex align-items-center">
                        <i className="fas fa-usd-circle card-body__icon me-2" />
                        <div className="ps-1">Commission</div>
                      </div>
                    </div>
                    <div className="fw-bold">
                      {price(accommodation.pre_adjusted_commission_in_cents)}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        <hr />
        <div className="mb-3 clearfix text-small">
          <p className="fw-bold mb-2">Check-in</p>
          <div className="d-flex align-items-center">
            <i className="fas fa-calendar-alt card-body__icon" />
            <span className="ps-2">
              <FormattedTime
                date={accommodation.first_travel_date._value}
                time={accommodation.check_in}
              />
            </span>
          </div>
          <p className="fw-bold mb-2 mt-2">Check-out</p>
          <div className="d-flex align-items-center">
            <i className="fas fa-calendar-alt card-body__icon" />
            <span className="ps-2">
              <FormattedTime
                date={accommodation.checkout_date?._value}
                time={accommodation.check_out}
              />
            </span>
          </div>
        </div>
        {accommodations.length - 1 !== accommodationIndex && <hr />}
      </div>
    ))}
  </Card.Body>
);

export const AccommodationView = ({ onlyShowInfo = false }) => {
  const dispatch = useDispatch();
  const currentBooking = useSelector(getCurrentBooking);
  const passengerTypes = useSelector(getAllPassengerTypes);
  const { isActive, isFullyPaid } = useCurrentBookingState();
  const [isRemoving, setIsRemoving] = useState(false);

  const handleRemove = (reservationId) => {
    setIsRemoving(true);

    dispatch(deleteReservation(currentBooking.id, reservationId)).finally(() =>
      setIsRemoving(true)
    );
  };

  const accommodations = getReservationsByProductType(
    'accommodation',
    currentBooking
  );

  const experienceIncludesAccommodation = someExperienceIncludesProductType(
    'accommodation',
    currentBooking
  );

  const totalCost =
    getTotalGrossInCents(accommodations) -
    sumBy(accommodations, 'pre_adjusted_commission_in_cents');

  const [accommodationOpen, setAccommodationOpen] = useState(true);

  let cardBody = null;
  let showSubtotal = false;

  if (isRemoving) {
    cardBody = <DeletingReservation />;
  } else if (experienceIncludesAccommodation) {
    cardBody = <ExperienceIncludesAccommodation />;
  } else {
    showSubtotal = true;

    cardBody = (
      <ReservationsList
        accommodations={accommodations}
        currentBooking={currentBooking}
        passengerTypes={passengerTypes}
        handleRemove={handleRemove}
        isFullyPaid={isFullyPaid}
        isActive={isActive}
      />
    );
  }

  // Auto expend section on receipt page
  useEffect(() => {
    setAccommodationOpen(accommodationOpen || isFullyPaid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullyPaid]);

  // This is for booking detail page, no header or footer in this case
  if (onlyShowInfo) {
    return cardBody;
  }

  return (
    <Card border="primary">
      <Card.Header
        bg="primary"
        onClick={() => setAccommodationOpen(!accommodationOpen)}
      >
        <div data-testid="accommodation-header">Accommodation</div>
        <div>
          {totalCost !== 0 && <span>{price(totalCost)}</span>}
          <i
            className={`fas ms-3 ${
              accommodationOpen ? 'fa-chevron-up' : 'fa-chevron-down'
            }`}
          />
        </div>
      </Card.Header>
      <Collapse in={accommodationOpen}>
        <div>
          {cardBody}
          {showSubtotal && (
            <Card.Footer>
              <div className="fw-bold">Subtotal</div>
              <div className="fw-bold">{totalCost ? price(totalCost) : ''}</div>
            </Card.Footer>
          )}
        </div>
      </Collapse>
    </Card>
  );
};

export const AccommodationViewNoDate = () => {
  const currentBooking = useSelector(getCurrentBooking);
  const [accommodationOpen, setAccommodationOpen] = useState(true);

  return (
    <Card border="primary">
      <Card.Header
        bg="primary"
        onClick={() => setAccommodationOpen(!accommodationOpen)}
      >
        <div>Accommodation</div>
        <div>
          <i
            className={`fas ${
              accommodationOpen ? 'fa-chevron-up' : 'fa-chevron-down'
            }`}
          />
        </div>
      </Card.Header>
      <Collapse in={accommodationOpen}>
        {showUpSell('accommodation', currentBooking) ? (
          <Card.Body className="card-body--no-data text-grey">
            Looking for somewhere to stay?
            <div>
              <a
                href={`${ecom.craftConfig.api_prefix}/accommodation`}
                className="btn btn-outline-primary"
              >
                View accommodation
              </a>
            </div>
          </Card.Body>
        ) : (
          <div>
            <Card.Body className="card-body--no-data">
              Select your ferry, <br />
              then add accommodation
            </Card.Body>
          </div>
        )}
      </Collapse>
    </Card>
  );
};

FormattedTime.propTypes = {
  date: PropTypes.string,
  time: PropTypes.string,
};

ReservationsList.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  accommodations: PropTypes.arrayOf(PropTypes.object),
  // eslint-disable-next-line react/forbid-prop-types
  currentBooking: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  passengerTypes: PropTypes.object,
  handleRemove: PropTypes.func,
  isFullyPaid: PropTypes.bool,
  isActive: PropTypes.bool,
};

AccommodationView.propTypes = {
  onlyShowInfo: PropTypes.bool,
};
